import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoginComponent } from './views/login/login.component';
import { GlobalPositionComponent } from './views/global-position/global-position.component';
import { CarouselComponent } from './views/carousel/carousel.component';
import { HeaderComponent } from './components/header/header.component';
import { TermsAndConditionsComponent } from './views/terms-and-conditions/terms-and-conditions.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { languages } from './shared/languages';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

// Materials
import { MaterialModules } from './material-modules';

//Pipes
import { SafeHtmlPipe } from './safe-html.pipe'; // Import the pipe

// components
import { SearchEntitiesComponent } from './components/search-entities/search-entities.component';
import { FaqsComponent } from './views/faqs/faqs.component';
import { ModalLoginComponent } from './components/modals/modal-login/modal-login.component';
import { ModalTermsComponent } from './components/modals/modal-terms/modal-terms.component';
import { ModalPolityComponent } from './components/modals/modal-polity/modal-polity.component';
import { LoadingComponent } from './components/loading/loading.component';
import { OnlyNumbersDirective } from './directives/only-numbers.directive';
import { ErrorServerComponent } from './components/error-server/error-server.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { NgxCaptchaModule } from 'ngx-captcha';
import { ScaModalComponent } from './components/modals/sca-modal/sca-modal.component';
import { HomeComponent } from './views/home/home.component';
import { UnsubscribeComponent } from './components/modals/unsubscribe/unsubscribe.component';
import { UnsubscribeSuccessComponent } from './components/unsubscribe-success/unsubscribe-success.component';
import { TipsComponent } from './views/global-position/tips/tips.component';
import { ReportComponent } from './views/global-position/report/report.component';
import { ErrorComponent } from './components/error/error.component';
import { HeaderDesktopComponent } from './components/header-desktop/header-desktop.component';
import { SummaryComponent } from './views/global-position/summary/summary.component';
import { FaqsClientComponent } from './views/global-position/faqs-client/faqs-client.component';
import { EngagementComponent } from './views/engagement/engagement.component';
import { AddDocumentationComponent } from './views/engagement/views-engagement/add-documentation/add-documentation.component';
import { SuccessProcessComponent } from './views/engagement/views-engagement/success-process/success-process.component';
import { ManegementPortalComponent } from './views/manegement-portal/manegement-portal.component';
import { AdminPortalComponent } from './views/admin-portal/admin-portal.component';
import { ResumeEurobitsComponent } from './views/admin-portal/resume-eurobits/resume-eurobits.component';
import { UpdateParamsComponent } from './views/admin-portal/update-params/update-params.component';
import { AuditComponent } from './views/admin-portal/audit/audit.component';
import { LogsComponent } from './views/admin-portal/logs/logs.component';
import { ManualComponent } from './views/admin-portal/resume-eurobits/manual/manual.component';
import { AutoComponent } from './views/admin-portal/resume-eurobits/auto/auto.component';
import { ServiceComponent } from './views/admin-portal/update-params/service/service.component';
import { CategoriesComponent } from './views/admin-portal/update-params/categories/categories.component';
import { AdminEngagementComponent } from './views/admin-portal/update-params/engagement/engagement.component';
import { KpisAdminComponent } from './views/admin-portal/update-params/kpis-admin/kpis-admin.component';
import { ThermometerComponent } from './components/thermometer/thermometer.component';
import { DetailComponent } from './views/global-position/summary/detail/detail.component';
import { MainComponent } from './views/global-position/summary/main/main.component';
import { ModalSummaryComponent } from './components/modals/modal-summary/modal-summary.component';
import { WarningTermsPolityComponent } from './components/modals/warning-terms-polity/warning-terms-polity.component';
import { FooterComponent } from './components/footer/footer.component';
import { ModalCookiesComponent } from './components/modals/modal-cookies/modal-cookies.component';
import { StatsComponent } from './views/global-position/stats/stats.component';
import { SecurityComponent } from './views/security/security.component';
import { SecurityBannerComponent } from './components/security-banner/security-banner.component';
import { SigninComponent } from './views/signin/signin.component';
import { UserSingInComponent } from './views/admin-portal/user-sing-in/user-sing-in.component';
import { ScoringComponent } from './views/scoring/scoring.component';
import { SettingsComponent } from './views/global-position/settings/settings.component';
import { DeleteAcountComponent } from './components/modals/delete-acount/delete-acount.component';
import { LoadingAkanaComponent } from './components/loading-akana/loading-akana.component';
import { SessionEndComponent } from './components/modals/session-end/session-end.component';
import { SigninSCFComponent } from './views/signin-scf/signin-scf.component';
import { Signin2Component } from './views/signin2/signin2.component';
import { ForgetPassComponent } from './views/signin2/forget-pass/forget-pass.component';
import { MultiLoginComponent } from './views/signin2/multi-login/multi-login.component';
import { TestComponent } from './views/test/test.component';
import { WidgetComponent } from './views/widget/widget.component';
import { AdminReportComponent } from './views/admin-portal/update-params/report/report.component';
import { EditReportComponent } from './views/admin-portal/edit-report/edit-report.component';
import { AdminModalConfirmComponent } from './views/admin-portal/admin-modal-confirm/admin-modal-confirm.component';
import { EditAdminFieldComponent } from './views/admin-portal/edit-admin-field/edit-admin-field.component';
import { WarningLoginComponent } from './components/modals/warning-login/warning-login.component';
import { TinkResponseComponent } from './views/tink-response/tink-response.component';
import { RapidErrorComponent } from './components/modals/rapid-error/rapid-error.component';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { LanguageSelectorComponent } from './language-selector/language-selector.component';
import { LanguageSelectorBeComponent } from 'src/app/language-selector-be/language-selector-be.component';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';

const languageMapping: { [key: string]: string } = {
    'es': 'es_ES',
    'en': 'en_UK',
    'fr': 'fr_FR',
    'pt': 'pt_PT',
    'gr': 'gr_GR',
    'nl': 'nl_NL'
  };

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        GlobalPositionComponent,
        CarouselComponent,
        HeaderComponent,
        TermsAndConditionsComponent,
        PageNotFoundComponent,
        SearchEntitiesComponent,
        FaqsComponent,
        TinkResponseComponent,
        ModalLoginComponent,
        ModalTermsComponent,
        ModalPolityComponent,
        ScaModalComponent,
        LoadingComponent,
        OnlyNumbersDirective,
        ErrorServerComponent,
        NavbarComponent,
        HomeComponent,
        UnsubscribeComponent,
        UnsubscribeSuccessComponent,
        TipsComponent,
        ReportComponent,
        ErrorComponent,
        HeaderDesktopComponent,
        SummaryComponent,
        FaqsClientComponent,
        EngagementComponent,
        AddDocumentationComponent,
        SuccessProcessComponent,
        ManegementPortalComponent,
        AdminPortalComponent,
        ResumeEurobitsComponent,
        UpdateParamsComponent,
        AuditComponent,
        LogsComponent,
        ManualComponent,
        AutoComponent,
        ServiceComponent,
        CategoriesComponent,
        AdminEngagementComponent,
        KpisAdminComponent,
        ThermometerComponent,
        DetailComponent,
        MainComponent,
        ModalSummaryComponent,
        WarningTermsPolityComponent,
        FooterComponent,
        ModalCookiesComponent,
        StatsComponent,
        SecurityComponent,
        SecurityBannerComponent,
        SigninComponent,
        UserSingInComponent,
        ScoringComponent,
        SettingsComponent,
        DeleteAcountComponent,
        LoadingAkanaComponent,
        SessionEndComponent,
        SigninSCFComponent,
        Signin2Component,
        ForgetPassComponent,
        MultiLoginComponent,
        TestComponent,
        WidgetComponent,
        AdminReportComponent,
        EditReportComponent,
        EditAdminFieldComponent,
        AdminModalConfirmComponent,
        WarningLoginComponent,
        RapidErrorComponent,
        LanguageSelectorBeComponent,
        LanguageSelectorComponent
    ],
    imports: [
        MaterialModules,
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        HttpClientModule,
        MatNativeDateModule,
        SafeHtmlPipe,
        DynamicDialogModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: translateLoaderFactory,
                deps: [HttpClient]
            }
        }),
        NgxCaptchaModule
        // SocialLoginModule,
    ], providers: [
        DialogService,
        {
            provide: 'routePermissions',
            useValue: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => true
        },
        { provide: 'LANGUAGES', useValue: languages }
        // {
        //   provide: AuthServiceConfig,
        //   useFactory: provideConfig
        // }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
  constructor(private readonly translate: TranslateService) {
    // Setting default language as Spanish
    const browserLang = navigator.language.split('-')[0];
    const defaultLang = languageMapping[browserLang] || 'es_ES';
    const languageCodes = languages.map(lang => lang.code);
    translate.addLangs(languageCodes);
    translate.setDefaultLang(defaultLang);
    sessionStorage.setItem('language', defaultLang);
  }
}

/**
 * Translates loader factory
 * @param http 
 * @returns  
 */
export function translateLoaderFactory(http: any) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
